import type { Ref } from 'vue';
import type { CountryTag } from '@/shared/api/experience';
import { getCountryUrl } from '@/entities/rubrics';
import { useI18n } from '@experience/i18n';
import ru from '@/widgets/rubricsBlock/i18n/ru.json';
import type { ContextType, ListingTagMap } from '@/entities/rubrics';

export function useTourCountryTagsAdapters(
  rubricsTags: Ref<CountryTag[]>,
  popupTags: Ref<CountryTag[]>,
  context: Ref<ContextType>,
) {
  const { t } = useI18n({
    inheritLocale: true,
    messages: {
      ru,
    },
  });

  const rubricsAdapters = computed(() => {
    return rubricsTags.value.map((rubric) => ({
      value: rubric.tag_slug,
      label: rubric.name || '',
      id: rubric?.tag_id || undefined,
      counter: rubric?.counters?.[`${context.value}_count`] || 0,
      url: getCountryUrl(context.value, rubric?.country_slug || '', rubric.tag_slug),
      is_tours_included: false,
    }));
  });

  const popupTagsAdapter = computed(() =>
    popupTags.value.map((tag) => ({
      ...tag,
      value: tag.tag_slug,
      label: tag.name || '',
      id: tag?.tag_id || 0,
      counter: tag?.counters?.[`${context.value}_count`] || 0,
      url: getCountryUrl(context.value, tag?.country_slug || '', tag.tag_slug),
      seoUrl: tag?.urls?.tours || '',
      is_tours_included: false,
      href: '',
    })),
  );

  const popupTagsMapped = computed(() => {
    const wrongCategories = new Set([t('rubrics_section.noCategory'), t('rubrics_section.noMentioned')]);
    const accMap = new Map<string, ListingTagMap>();

    return popupTagsAdapter.value.reduce<ListingTagMap[]>((acc, tag) => {
      const category = tag?.category;
      if (tag.counter === 0 || !category || wrongCategories.has(category)) {
        return acc;
      }

      let categoryObj = accMap.get(category);
      if (!categoryObj) {
        categoryObj = { title: category, tags: [] };
        acc.push(categoryObj);
        accMap.set(category, categoryObj);
      }

      categoryObj.tags.push(tag);

      return acc;
    }, []);
  });

  return { popupTagsMapped, rubricsAdapters };
}
